import { useState } from 'react';
import classNames from 'classnames';
import { c, t } from 'ttag';

import MessageInfoIcon from '@svg/message-info.svg';
import ArrowRightIcon from '@svg/arrows-right.svg';
import { useRegion } from '@hook/useRegion';
import { usePlatformAuthorizeInfo } from '@hook/usePlatformAuthorizeInfo';
import { Image } from '@ui/Image';
import { Button } from '@ui/Button';
import { PromotionVariantsInfoModal } from '@components/PromotionVariantsInfoModal';
import { PhoneVerificationModalContainer } from '@containers/PhoneVerificationModal';

import s from './PromotionPackageCard.sss';

export interface PromotionPackage {
  id: number,
  label?: string | null,
  description?: string | null,
  isOpenedByDefault?: boolean | null,
  price: string,
  discount?: number | null,
  originalPrice: string,
  bonusPrice?: string | null,
  promotions?: ({
    title: string,
    icon?: string | null,
  } | null)[] | null,
}

interface Props {
  className?: string,
  theme?: 'default' | 'details',
  promotionPackage: PromotionPackage,
  isExpand?: boolean,
  isActive?: boolean,
  isDisabled?: boolean,
  onSubmit: (promotionPackage: PromotionPackage) => void,
  onExpand?: () => void,
}

export const PromotionPackageCard: React.FC<Props> = ({
  className,
  theme = 'default',
  promotionPackage,
  isExpand = true,
  isActive = false,
  isDisabled,
  onSubmit,
  onExpand,
}) => {
  const { currencyName } = useRegion();
  const [isPhoneVerificationModalOpen, setIsPhoneVerificationModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);


  const {
    loading: platformAuthorizeInfoLoading,
    checkIsNeedPhoneVerification,
  } = usePlatformAuthorizeInfo();

  const handleSubmit = async () => {
    const isNeedPhoneVerification = await checkIsNeedPhoneVerification();
  
    if (isNeedPhoneVerification) {
      setIsPhoneVerificationModalOpen(true);
    } else if (promotionPackage) {
      onSubmit(promotionPackage);
    }
  };

  const {
    label,
    description,
    price,
    originalPrice,
    bonusPrice,
    discount,
    promotions,
  } = promotionPackage;

  return (
    <>
      <div
        className={classNames(s.root, className, {
          [s.rootThemeDetails]: theme === 'details',
          [s.rootStateExpanded]: isExpand && isActive,
        })}
      >
        <div className={s.header}>
          <div>
            <div className={s.title}>
              {label}
              <Button
                className={s.infoButton}
                theme="text"
                onClick={() => setIsInfoModalOpen(true)}
              >
                <MessageInfoIcon
                  className={s.infoIcon}
                  width="16"
                  height="16"
                />
              </Button>
            </div>
            <p className={s.description}>
              {description}
            </p>
          </div>

          {!!onExpand && (
          <Button
            className={s.arrowButton}
            theme="text"
            onClick={onExpand}
          >
            <ArrowRightIcon
              className={classNames(s.arrowIcon, {
                [s.arrowIconStateOpen]: isExpand,
              })}
              width="24"
              height="24"
            />
          </Button>
          )}
        </div>
        {isExpand && (
        <>
          <div className={s.main}>
            <div className={s.priceBlock}>
              {Number(bonusPrice) ? (
                <>
                  <p className={s.price}>
                    {price}
                    &nbsp;
                    <span className={s.currency}>
                      {currencyName}
                    </span>
                  </p>
                  {price !== originalPrice && (
                    <p className={s.bonusPrice}>
                      {t`+ ${bonusPrice} ₴ бонусних`}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className={s.price}>
                    {price}
                    &nbsp;
                    <span className={s.currency}>
                      {currencyName}
                    </span>
                  </p>
                  {price !== originalPrice && (
                    <div className={s.discountBlock}>
                      <p className={s.originalPrice}>
                        <span className={s.oldPrice}>
                          {`${originalPrice} ${currencyName}`}
                        </span>
                      </p>
                      <p className={s.discount}>
                        {`-${discount}%`}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <ul className={s.benefitsList}>
            {promotions?.map((promotion, i) => {
              if (promotion) {
                return (
                  <li
                    key={i}
                    className={s.benefitsItem}
                  >
                    <Image
                      className={s.benefitsIcon}
                      src={promotion?.icon || ''}
                      width="24"
                      height="24"
                      alt="icon"
                    />
                    <p dangerouslySetInnerHTML={{ __html: promotion.title }} />
                  </li>
                );
              }

              return null;
            })}
          </ul>
          <Button
            className={s.button}
            onClick={handleSubmit}
            disabled={isDisabled || platformAuthorizeInfoLoading}
          >
            {c('PromotionPackageCard').t`Оплатити · ${price} ${currencyName}`}
          </Button>
        </>
        )}

      </div>

      <PromotionVariantsInfoModal
        isOpen={isInfoModalOpen}
        onRequestClose={() => setIsInfoModalOpen(false)}
      />

      <PhoneVerificationModalContainer
        isOpen={isPhoneVerificationModalOpen}
        onClose={() => setIsPhoneVerificationModalOpen(false)}
        description={t`Щоб просунути оголошення, верифікуйте номер телефону`}
        onVerify={handleSubmit}
      />
    </>
  );
};
